'use client';
import { Box } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridDensity,
  GridRowSelectionModel,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridSortModel,
} from '@mui/x-data-grid-pro';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { ActivityTypeDto, type ReportingCategoryDto, type SampleDataPackageRefDto } from '@/api/ApiClient';
import {
  isReportingCategoryGridEditable,
  reportingCategoryGridEditDisabledReason,
} from '@/lib/utils/isReportingCategoryGridEditable';
import { useBackend } from '@/hooks/useBackend';
import { InlineEditSelectCell } from '@/components/tables/bulk/cells/InlineEditCells';
import { useSampleListFilterStore } from '@/hooks/useSampleListFilterStore';
import type { SampleListMode } from '@/lib/sampleList/sampleListMode';
import { loadStoredColumnVisibility, COLUMN_VISIBILITY_STORAGE_KEY } from '@/stores/sampleFilterStore';
import {
  loadSubstrateStoredColumnVisibility,
  substrateDefaultColumnVisibilityModel,
  SUBSTRATE_COLUMN_VISIBILITY_STORAGE_KEY,
  SUBSTRATE_SORT_MODEL_STORAGE_KEY,
} from '@/stores/substrateFilterStore';
import { readUserScopedJson, writeUserScopedJson } from '@/lib/browser/userScopedStorage';
import { trimSampleIdFilter } from '@/lib/utils/prepareSampleSearchCriteriaForApi';
import { sampleGridSearchCriteria } from '@/lib/utils/sampleSearchKind';
import {
  filterSampleGridColumnsForListMode,
  filterSampleGridColumnsForSearchKind,
  filterSortModelForListMode,
  preserveOmittedDataPackageVisibility,
  sampleGridShowsDataPackageColumn,
  sanitizeSubstrateColumnVisibilityModel,
} from '@/lib/tables/sampleGridColumns';

const SAMPLE_SORT_MODEL_STORAGE_KEY = 'sampleGridSortModel';

const loadStoredSortModel = (storageKey: string, listMode: SampleListMode = 'samples'): GridSortModel => {
  try {
    const stored = readUserScopedJson(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate that it's an array and has the expected structure
      if (
        Array.isArray(parsed) &&
        parsed.every(
          (item) =>
            typeof item === 'object' && typeof item.field === 'string' && (item.sort === 'asc' || item.sort === 'desc')
        )
      ) {
        return filterSortModelForListMode(parsed, listMode);
      }
    }
  } catch (error) {
    log.warn('Failed to load stored sort model:', error);
  }
  // Return default sort model
  return [{ field: 'sampleLabel', sort: 'asc' }];
};
import { log } from '@/lib/utils/logger';
import { dataGridStyle } from '@/styles/tableStyles';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { SampleGridToolbar } from '@/components/tables/SampleGridToolbar';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { getActivityCategoryDisplay, getActivityTypeDisplay } from '@/lib/types/activityTypes';
import { findTechniqueById } from '@/lib/utils/optionUtils';
import { removeLeadingTrailingBrTags } from '@/lib/utils/textUtils';
import { useErrorSnackbar } from '@/hooks/useErrorSnackbar';
import { useGridSearchErrorHandler } from '@/hooks/useGridSearchErrorHandler';
import { consumeNextSearchUserInitiated } from '@/stores/searchUiStore';
import {
  buildSampleGridSnapshotToken,
  invalidateSampleGridCache,
  setCachedSampleGridSnapshot,
  tryGetCachedSampleGridSnapshot,
} from '@/lib/cache/sampleGridCache';
import { buildDataPackageHref, buildProjectSampleHref, buildSampleHref } from '@/lib/routing/appPaths';

// Helper functions moved outside component
const getStatusLabel = (key: string, options: any) => {
  return options.status?.find((entry: any) => entry.key === key)?.value || key;
};

const getSynthesisTechniqueName = (id: number | string, options: any) => {
  const idStr = id.toString();
  return findTechniqueById(options.synthesisTechniques, idStr)?.name || idStr;
};

const getCharacterizationTechniqueName = (id: number | string, options: any) => {
  const idStr = id.toString();
  return options.characterizationTechniques?.find((entry: any) => entry.id === idStr)?.name || idStr;
};

const getFacultyName = (userId: string, options: any) => {
  const user = options.staff?.find((u: any) => u.id === userId);
  if (!user?.faculty) return 'N/A';

  const facultyUser = options.staff?.find((u: any) => u.id === user.faculty);
  return facultyUser ? `${facultyUser.firstName ?? ''} ${facultyUser.lastName ?? ''}`.trim() : user.faculty;
};

// Helper function to render "N/A" values in italics
const renderCellValue = (value: string) => {
  if (value === 'N/A') {
    return <span style={{ fontStyle: 'italic' }}>{value}</span>;
  }
  return value;
};

function dataPackageRouteKey(pkg: SampleDataPackageRefDto): string {
  return pkg.routeKey?.trim() || String(pkg.id);
}

function dataPackageCellLabel(pkg: SampleDataPackageRefDto): string {
  return pkg.label?.trim() || `Data package ${pkg.id}`;
}

/** Data-package links for the sample grid. Not a project substitute. */
function renderDataPackageCell(packages: SampleDataPackageRefDto[], basePath: string | undefined, showLinks: boolean) {
  if (packages.length === 0) {
    return renderCellValue('N/A');
  }
  if (!showLinks) {
    return renderCellValue(packages.map(dataPackageCellLabel).join(', '));
  }
  return (
    <>
      {packages.map((pkg, index) => (
        <span key={pkg.id}>
          <Link href={buildDataPackageHref(basePath, dataPackageRouteKey(pkg))}>{dataPackageCellLabel(pkg)}</Link>
          {index < packages.length - 1 ? ', ' : null}
        </span>
      ))}
    </>
  );
}

/**
 * Maps DataGrid `field` values to API `sortColumn` names (see server SampleSearchHelper.ApplyOrderByInDataBase).
 */
function mapSampleGridSortFieldToBackend(field: string): string {
  switch (field) {
    case 'parentLabel':
      return 'growthLabel';
    case 'growthDates':
      return 'firstGrowthDate';
    default:
      return field;
  }
}

const getActivityTypeLabel = (activity: any) => {
  if (!activity) return 'N/A';

  const category = activity.category && getActivityCategoryDisplay(activity.category);
  const type = activity.type && getActivityTypeDisplay(activity.type);

  // Split activities show only the type label ('Split'), not the "Processing - ..." combination below.
  if (activity.type === ActivityTypeDto.Dicing) return type;

  // For processing and characterization, show both category and type
  if ((category === 'Processing' || category === 'Characterization') && type) {
    return `${category} - ${type}`;
  }

  // Fallback to category or type
  return category || type || 'N/A';
};

export type SampleRowType = {
  id: number;
  projectIds?: number[];
  /** Raw project labels for this row — used with inline reporting category edits. */
  projectLabels?: string[];
  /** Server flag: when true, reporting category may be edited in the grid (in-house / unassigned). */
  reportingCategoryRequired?: boolean;
  /** Server flag: current user may update this sample (Sample.Update with project/unassigned rules). */
  canUpdateSample?: boolean;
  sampleLabel: string;
  alias?: string;
  /** Parent (growth run) label when sample is part of a multi-sample growth run. */
  parentLabel?: string | null;
  isSnapshot?: boolean;
  dataPackages?: SampleDataPackageRefDto[];
  creationDate: string;
  growthDate?: string;
  substrate: string;
  material: string;
  technique?: string;
  instrument?: string;
  lastActivity?: string;
  owner: string;
  addedBy: string;
  grownBy?: string[];
  synthesisTechniques?: string[];
  synthesisInstruments?: string[];
  growthDates?: string[];
  characterizationTechniques?: string[];
  characterizationInstruments?: string[];
  status: string;
  lastActivityDate: string;
  lastChangedDate: string;
  lastChangedBy: string;
  transferred: string;
  shipped: string;
  materials?: number[];
  faculty?: string;
  substrateName?: string;
  substrateVendor?: string;
  substrateLot?: string;
  substrateOrientation?: string;
  /** Display string for grid; 'N/A' when the API sends no purity. */
  substratePurity?: string;
  reportingCategory?: string;
  reportingCategoryId?: number | null;
};

interface SampleGridCoreProps {
  /** Samples page vs Substrates page (separate filter store and grid cache). */
  listMode?: SampleListMode;
  /** Whether to show links in sampleLabel column */
  showLinks?: boolean;
  /** Whether to enable row selection */
  enableSelection?: boolean;
  /** Selection model for controlled selection */
  selectionModel?: GridRowSelectionModel;
  /** Callback for selection changes */
  onSelectionChange?: (selection: GridRowSelectionModel) => void;
  /** Whether to show checkboxes for multiple selection */
  checkboxSelection?: boolean;
  /** Custom toolbar slot */
  toolbarSlot?: React.ComponentType<any>;
  /** Custom columns to override defaults */
  customColumns?: GridColDef[];
  /** Whether to disable column menu */
  disableColumnMenu?: boolean;
  /** Whether to show the toolbar (columns, density, export) */
  showToolbar?: boolean;
  /** Grid density setting */
  density?: GridDensity;
  /** Callback when a sample is selected (with full sample data) */
  onSampleSelect?: (sample: SampleRowType) => void;
  /** Callback when multiple samples are selected (with full sample data) */
  onSamplesSelect?: (samples: SampleRowType[]) => void;
  /** Options data - passed from parent to avoid useSelectOptions conflicts */
  optionsData?: {
    status?: any[];
    synthesisTechniques?: any[];
    materials?: any[];
    activityType?: any[];
    staff?: any[];
    sampleReportingCategory?: any[];
  };
  /** Whether options are loading */
  optionsLoading?: boolean;
  /** Sample IDs to disable (already in data package) — rows are grayed out and unselectable */
  disabledSampleIds?: number[];
}

export const SampleGridCore: FC<SampleGridCoreProps> = ({
  listMode = 'samples',
  showLinks = true,
  enableSelection = false,
  selectionModel,
  onSelectionChange,
  checkboxSelection = false,
  toolbarSlot,
  customColumns,
  disableColumnMenu = false,
  showToolbar = true,
  density: propDensity,
  onSampleSelect,
  onSamplesSelect,
  optionsData,
  optionsLoading = false,
  disabledSampleIds,
}) => {
  const backend = useBackend();
  const systemConfig = useSystemConfig();
  const { resolveUser } = useUserResolution();
  const { showErrorSnackbar } = useErrorSnackbar();
  const { handleGridSearchError } = useGridSearchErrorHandler(listMode === 'substrates' ? 'substrates' : 'samples');

  // Use optionsData passed from parent
  const finalOptionsLoading = optionsLoading || false;

  const sortStorageKey = listMode === 'substrates' ? SUBSTRATE_SORT_MODEL_STORAGE_KEY : SAMPLE_SORT_MODEL_STORAGE_KEY;
  const columnVisibilityStorageKey =
    listMode === 'substrates' ? SUBSTRATE_COLUMN_VISIBILITY_STORAGE_KEY : COLUMN_VISIBILITY_STORAGE_KEY;

  const { filters, paginationModel, setPaginationModel, setIsSearching, sampleGridApplyGeneration } =
    useSampleListFilterStore(listMode);
  const [rows, setRows] = useState<SampleRowType[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>(() => loadStoredSortModel(sortStorageKey, listMode));

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() =>
    listMode === 'substrates' ? loadSubstrateStoredColumnVisibility() : loadStoredColumnVisibility()
  );

  const [density, setDensity] = useState<GridDensity>(propDensity || 'standard');

  const handleColumnVisibilityChange = (newModel: GridColumnVisibilityModel) => {
    const columnPresent = sampleGridShowsDataPackageColumn(listMode, filters.kind);
    const withPreservedPackage = preserveOmittedDataPackageVisibility(columnVisibilityModel, newModel, columnPresent);
    const modelToStore =
      listMode === 'substrates'
        ? sanitizeSubstrateColumnVisibilityModel(withPreservedPackage, substrateDefaultColumnVisibilityModel)
        : withPreservedPackage;
    setColumnVisibilityModel(modelToStore);
    writeUserScopedJson(columnVisibilityStorageKey, JSON.stringify(modelToStore));
  };

  const handleSortModelChange = (newModel: GridSortModel) => {
    const filtered = filterSortModelForListMode(newModel, listMode);
    setSortModel(filtered);
    writeUserScopedJson(sortStorageKey, JSON.stringify(filtered));
  };

  // Only memoize the material map since it's larger and more complex
  const materialMap = useMemo(
    () =>
      new Map<number, string>(
        optionsData?.materials
          ?.filter((m) => m.id !== undefined && m.name !== undefined)
          .map((m) => [m.id as number, m.name as string])
      ),
    [optionsData?.materials]
  );

  const reportingCategorySelectOptions = useMemo(
    () =>
      (optionsData?.sampleReportingCategory as ReportingCategoryDto[] | undefined)
        ?.filter((o): o is ReportingCategoryDto & { id: number; label: string } => o?.id != null && Boolean(o.label))
        .map((o) => ({ id: o.id as number, label: o.label as string })) ?? [],
    [optionsData?.sampleReportingCategory]
  );

  const reportingCategoryIdToLabel = useMemo(() => {
    const map = new Map<number, string>();
    for (const o of reportingCategorySelectOptions) {
      map.set(o.id, o.label);
    }
    return map;
  }, [reportingCategorySelectOptions]);

  const [inlineCellState, setInlineCellState] = useState<Record<string, { saving?: boolean; error?: string | null }>>(
    {}
  );
  const setCellState = useCallback((key: string, patch: { saving?: boolean; error?: string | null }) => {
    setInlineCellState((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  const saveReportingCategory = useCallback(
    async (sampleId: number, nextCategoryId: number) => {
      const key = `${sampleId}:reportingCategory`;
      log.debug('[SampleGridCore] Saving reporting category', { sampleId, nextCategoryId });
      setCellState(key, { saving: true, error: null });
      try {
        await backend.sample.assignReportingCategoryToSample(String(sampleId), nextCategoryId);
        const rawLabel = reportingCategoryIdToLabel.get(nextCategoryId) ?? '';
        const displayLabel = rawLabel || 'N/A';
        setRows((prev) => {
          const next = prev.map((r) =>
            r.id === sampleId ? { ...r, reportingCategory: displayLabel, reportingCategoryId: nextCategoryId } : r
          );
          invalidateSampleGridCache();
          return next;
        });
        setCellState(key, { saving: false, error: null });
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Save failed';
        log.debug('[SampleGridCore] Failed to save reporting category', {
          sampleId,
          nextCategoryId,
          error: message,
        });
        setCellState(key, { saving: false, error: message });
        showErrorSnackbar(e);
      }
    },
    [backend.sample, reportingCategoryIdToLabel, setCellState, showErrorSnackbar]
  );

  const snapshotToken = useMemo(
    () =>
      buildSampleGridSnapshotToken({
        listMode,
        filters,
        paginationModel,
        sortModel,
        applyGeneration: sampleGridApplyGeneration,
      }),
    [listMode, filters, paginationModel, sortModel, sampleGridApplyGeneration]
  );

  const cachedSnapshot = useMemo(
    () => tryGetCachedSampleGridSnapshot(snapshotToken, listMode),
    [snapshotToken, listMode]
  );
  const visibleRows = cachedSnapshot?.rows ?? rows;
  const visibleTotalRows = cachedSnapshot?.totalRows ?? totalRows;
  const visibleLoading = finalOptionsLoading || (!cachedSnapshot && loading);

  // Fetch data when filters, pagination, sort, or options change
  useEffect(() => {
    // Don't fetch if options are still loading
    if (finalOptionsLoading) {
      log.debug('[SampleGridCore] Options still loading, skipping fetch');
      return;
    }

    // Check if we have all the required options loaded
    const hasAllOptions =
      optionsData?.status &&
      optionsData?.synthesisTechniques &&
      optionsData?.materials &&
      optionsData?.activityType &&
      optionsData?.staff &&
      optionsData?.sampleReportingCategory;

    if (!hasAllOptions) {
      log.debug('[SampleGridCore] Missing required options, skipping fetch:', {
        finalOptionsLoading,
        hasAllOptions,
        status: !!optionsData?.status,
        synthesisTechniques: !!optionsData?.synthesisTechniques,
        materials: !!optionsData?.materials,
        activityType: !!optionsData?.activityType,
        staff: !!optionsData?.staff,
        sampleReportingCategory: !!optionsData?.sampleReportingCategory,
      });
      return;
    }

    if (cachedSnapshot) {
      log.debug('[SampleGridCore] Using in-memory sample grid snapshot');
      return;
    }

    const fetchSamples = async () => {
      const userInitiatedSearch = consumeNextSearchUserInitiated();
      log.debug('[SampleGridCore] fetchSamples called');
      try {
        setIsSearching(true);
        setLoading(true);
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;
        const draw = paginationModel.page + 1;
        const sortColumn = mapSampleGridSortFieldToBackend(sortModel[0]?.field || 'sampleLabel');
        const sortOrder = sortModel[0]?.sort || 'asc';
        const criteria = {
          ...sampleGridSearchCriteria(filters, listMode),
          ...(listMode === 'substrates' ? { onlyWithoutSynthesisActivity: true } : {}),
          sampleId: trimSampleIdFilter(filters.sampleId),
          sampleLabel: trimSampleIdFilter(filters.sampleLabel),
        };

        const sampleList = await backend.sample.search(sortColumn, sortOrder, start, length, draw, criteria);

        const formattedRows: SampleRowType[] = sampleList.data.map((sample) => ({
          id: sample.id,
          sampleLabel: sample.sampleLabel,
          alias: sample.refSampleLabel || 'N/A',
          parentLabel: sample.parentLabel ?? null,
          isSnapshot: sample.isSnapshot ?? false,
          dataPackages: sample.dataPackages ?? [],
          projectLabels: [...sample.projectLabels],
          projectLabel: sample.projectLabels.join(', ') || 'N/A',
          projectId: sample.projectIds.join(', '),
          creationDate: new Date(sample.creationDate).toLocaleDateString(),
          substrate: sample.substrateMaterials.join(', ') || 'N/A',
          substrateName: sample.substrateName || 'N/A',
          substrateVendor: sample.substrateVendor || 'N/A',
          substrateLot: sample.substrateLot || 'N/A',
          substrateOrientation: sample.substrateOrientation || 'N/A',
          substratePurity: sample.substratePurity != null ? String(sample.substratePurity) : 'N/A',
          material:
            sample.materialIds
              .map((id: number) => materialMap.get(id))
              .filter(Boolean)
              .join(', ') || 'N/A',
          description: sample.description || null,
          lastActivity: getActivityTypeLabel(sample.lastActivity),
          lastActivityDate: sample.lastActivity?.date ? new Date(sample.lastActivity.date).toLocaleDateString() : 'N/A',
          lastActivityDesc: sample.lastActivity?.desc || '',
          lastChangedDate: new Date(sample.lastChangedDate).toLocaleString(),
          lastChangedBy: resolveUser(sample.lastChangedByUser) || sample.lastChangedByUser,
          transferred: sample.transferred ? 'Yes' : 'No',
          shipped: sample.shipped ? 'Yes' : 'No',
          owner: resolveUser(sample.userId) || sample.userId,
          addedBy: resolveUser(sample.addedBy) || sample.addedBy,
          grownBy: sample.grownByUsers.map((user) => resolveUser(user) || user),
          synthesisTechniquesNames: sample.synthesisTechniques.map((technique) =>
            getSynthesisTechniqueName(technique, optionsData)
          ),
          synthesisInstrumentsNames: sample.synthesisInstruments,
          characterizationTechniquesNames: sample.characterizationTechniques.map((technique) =>
            getCharacterizationTechniqueName(technique, optionsData)
          ),
          growthDates: sample.growthDates.map((date) => new Date(date).toLocaleDateString()),
          characterizationInstrumentsNames: sample.characterizationInstruments,
          faculty: getFacultyName(sample.userId, optionsData),
          status: getStatusLabel(sample.status, optionsData) || 'Not Grown',
          reportingCategory: sample.reportingCategory?.label || 'N/A',
          reportingCategoryId: sample.reportingCategory?.id ?? null,
          reportingCategoryRequired: sample.reportingCategoryRequired ?? false,
          canUpdateSample: sample.canUpdateSample ?? false,
        }));

        setRows(formattedRows ?? []);
        const total = sampleList?.recordsFiltered ?? 0;
        setTotalRows(total);
        setCachedSampleGridSnapshot(snapshotToken, formattedRows ?? [], total, listMode);
      } catch (error) {
        log.error('[SampleGridCore] Error fetching samples:', error);
        handleGridSearchError(error, { userInitiatedSearch });
      } finally {
        setLoading(false);
        setIsSearching(false);
      }
    };

    log.debug('[SampleGridCore] All options loaded, fetching data');
    fetchSamples();
  }, [
    backend.sample,
    filters,
    paginationModel,
    sortModel,
    sampleGridApplyGeneration,
    resolveUser,
    finalOptionsLoading,
    optionsData,
    materialMap,
    handleGridSearchError,
    setIsSearching,
    snapshotToken,
    cachedSnapshot,
    listMode,
  ]);

  // Handle sample selection with full data
  const handleSelectionChangeWithData = (newSelectionModel: GridRowSelectionModel) => {
    // Call the original onSelectionChange if provided
    onSelectionChange?.(newSelectionModel);

    // Handle sample selection with full data
    if (onSampleSelect && newSelectionModel.ids.size === 1) {
      const selectedId = Array.from(newSelectionModel.ids)[0];
      const selectedSample = visibleRows.find((row) => row.id === selectedId);
      if (selectedSample) {
        onSampleSelect(selectedSample);
      }
    }

    if (onSamplesSelect && newSelectionModel.ids.size > 0) {
      const selectedSamples = visibleRows.filter((row) => newSelectionModel.ids.has(row.id));
      onSamplesSelect(selectedSamples);
    }
  };

  const defaultColumns = useMemo<GridColDef[]>(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      },
      {
        field: 'sampleLabel',
        headerName: 'ID',
        flex: 1,
        minWidth: 150,
        renderCell: showLinks
          ? (params) => {
              const row = params.row as SampleRowType;
              const href = buildSampleHref(systemConfig?.basePath, row.isSnapshot ? row.id : (params.value as string));
              return <Link href={href}>{renderCellValue(params.value)}</Link>;
            }
          : (params) => renderCellValue(params.value),
      },
      {
        field: 'alias',
        headerName: 'Alias',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'parentLabel',
        headerName: 'Growth Run',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const label = params.value as string | null | undefined;
          // No parent growth run — show italic N/A (same as other empty columns), not a blank cell.
          if (!label) return renderCellValue('N/A');
          return (
            <Link href={`${systemConfig?.basePath}/samples/${encodeURIComponent(label)}`}>
              {renderCellValue(label)}
            </Link>
          );
        },
      },
      {
        field: 'projectLabel',
        headerName: 'Project',
        sortable: false,
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const row = params.row as SampleRowType;
          const labels = row.projectLabels;
          if (!labels || labels.length === 0) {
            return renderCellValue('N/A');
          }
          if (!showLinks) {
            return renderCellValue(labels.join(', '));
          }
          return (
            <>
              {labels.map((label, index) => (
                <span key={`${label}-${index}`}>
                  <Link href={buildProjectSampleHref(systemConfig?.basePath, label, row.sampleLabel)}>{label}</Link>
                  {index < labels.length - 1 ? ', ' : null}
                </span>
              ))}
            </>
          );
        },
      },
      {
        field: 'dataPackages',
        headerName: 'Data package',
        sortable: false,
        flex: 1,
        minWidth: 150,
        valueGetter: (_value, row) => {
          const packages = (row as SampleRowType).dataPackages ?? [];
          if (packages.length === 0) return 'N/A';
          return packages.map(dataPackageCellLabel).join(', ');
        },
        renderCell: (params) => {
          const row = params.row as SampleRowType;
          return renderDataPackageCell(row.dataPackages ?? [], systemConfig?.basePath, showLinks);
        },
      },
      {
        field: 'creationDate',
        headerName: 'Creation Date',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'substrate',
        headerName: 'Substrate Material',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'material',
        headerName: 'Growth Material',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'reportingCategory',
        headerName: 'Reporting Category',
        flex: 1,
        minWidth: 150,
        sortable: false,
        disableClickEventBubbling: true,
        renderCell: (params) => {
          const row = params.row as SampleRowType;
          const sampleId = row.id;
          // Pass the row label through (including 'N/A' when unset). InlineEditSelectCell renders empty/unset as italic N/A.
          const valueLabel = row.reportingCategory ?? 'N/A';
          const valueId = row.reportingCategoryId ?? null;
          const key = `${sampleId}:reportingCategory`;
          const editable = isReportingCategoryGridEditable({
            reportingCategoryRequired: row.reportingCategoryRequired,
            canUpdateSample: row.canUpdateSample,
          });
          const disabledReason = reportingCategoryGridEditDisabledReason({
            reportingCategoryRequired: row.reportingCategoryRequired,
            canUpdateSample: row.canUpdateSample,
          });

          return (
            <Box sx={{ width: '100%' }} title={!editable ? disabledReason : undefined}>
              <InlineEditSelectCell
                valueId={valueId}
                valueLabel={valueLabel}
                options={reportingCategorySelectOptions}
                onSaveId={(nextId) => saveReportingCategory(sampleId, nextId)}
                saveState={inlineCellState[key]}
                disabled={!editable || finalOptionsLoading || reportingCategorySelectOptions.length === 0}
              />
            </Box>
          );
        },
      },
      {
        field: 'lastActivity',
        headerName: 'Last Activity',
        flex: 1,
        sortable: false,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'lastActivityDate',
        headerName: 'Last Activity Date',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'lastChangedDate',
        headerName: 'Last Changed',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'lastChangedBy',
        headerName: 'Changed By',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      { field: 'transferred', headerName: 'Transferred', flex: 1, sortable: false, minWidth: 150 },
      { field: 'shipped', headerName: 'Shipped', flex: 1, sortable: false, minWidth: 150 },
      {
        field: 'owner',
        headerName: 'Owner',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'addedBy',
        headerName: 'Added By',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'grownBy',
        headerName: 'Grown By',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          const grownBy = params.value;
          if (!grownBy || grownBy.length === 0) return 'N/A';
          return grownBy.map((user: string) => renderCellValue(user)).join(', ');
        },
      },
      {
        field: 'synthesisTechniquesNames',
        headerName: 'Synthesis Techniques',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          const techniques = params.value;
          if (!techniques || techniques.length === 0) return 'N/A';
          return techniques
            .map((technique: string | number) => getSynthesisTechniqueName(technique, optionsData))
            .join(', ');
        },
      },
      {
        field: 'synthesisInstrumentsNames',
        headerName: 'Synthesis Instruments',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          const instruments = params.value;
          if (!instruments || instruments.length === 0) return 'N/A';
          return instruments.join(', ');
        },
      },
      {
        field: 'growthDates',
        headerName: 'Growth Dates',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const dates = params.value;
          if (!dates || dates.length === 0) return 'N/A';
          return dates.join(', ');
        },
      },
      {
        field: 'characterizationTechniquesNames',
        headerName: 'Characterization Techniques',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          const techniques = params.value;
          if (!techniques || techniques.length === 0) return 'N/A';
          return techniques
            .map((technique: string | number) => getCharacterizationTechniqueName(technique, optionsData))
            .join(', ');
        },
      },
      {
        field: 'characterizationInstrumentsNames',
        headerName: 'Characterization Instruments',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          const instruments = params.value;
          if (!instruments || instruments.length === 0) return 'N/A';
          return instruments.join(', ');
        },
      },
      {
        field: 'faculty',
        headerName: 'Faculty',
        flex: 1,
        sortable: false,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      { field: 'status', headerName: 'Sample Status', flex: 1, sortable: false, minWidth: 150 },
      {
        field: 'substrateName',
        headerName: 'Substrate Name',
        flex: 1,
        sortable: false,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'substrateVendor',
        headerName: 'Substrate Vendor',
        flex: 1,
        sortable: false,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'substrateLot',
        headerName: 'Substrate Lot',
        flex: 1,
        sortable: false,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'substrateOrientation',
        headerName: 'Substrate Orientation',
        flex: 1,
        sortable: false,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'substratePurity',
        headerName: 'Substrate Purity (%)',
        flex: 1,
        sortable: false,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
    ],
    [
      showLinks,
      systemConfig?.basePath,
      optionsData,
      saveReportingCategory,
      reportingCategorySelectOptions,
      inlineCellState,
      finalOptionsLoading,
    ]
  );

  const columns = useMemo(() => {
    const base = customColumns || defaultColumns;
    return filterSampleGridColumnsForSearchKind(
      filterSampleGridColumnsForListMode(base, listMode),
      listMode,
      filters.kind
    );
  }, [customColumns, defaultColumns, listMode, filters.kind]);

  return (
    <DataGridPro
      rows={visibleRows}
      columns={columns}
      loading={visibleLoading}
      rowCount={visibleTotalRows}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityChange}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={[25, 50, 100]}
      disableRowSelectionOnClick={!enableSelection}
      disableColumnMenu={disableColumnMenu}
      showToolbar={showToolbar}
      density={density}
      onDensityChange={(newDensity) => setDensity(newDensity)}
      slots={{ toolbar: showToolbar ? toolbarSlot || SampleGridToolbar : undefined }}
      getRowHeight={() => 'auto'}
      checkboxSelection={checkboxSelection}
      onRowSelectionModelChange={handleSelectionChangeWithData}
      rowSelectionModel={selectionModel}
      isRowSelectable={(params) => !disabledSampleIds?.includes(params.row.id)}
      getRowClassName={(params) => (disabledSampleIds?.includes(params.row.id) ? 'row-disabled' : '')}
      initialState={{
        pinnedColumns: { left: [GRID_DETAIL_PANEL_TOGGLE_FIELD, 'sampleLabel'] },
      }}
      getDetailPanelContent={({ row }) => {
        // Clean and trim descriptions to remove leading/trailing <br> tags and whitespace
        const sampleDesc = removeLeadingTrailingBrTags(row.description || '');
        const activityDesc = row.lastActivityDesc?.trim();

        // Ignore descriptions with just empty tags like "<br>" or empty after cleaning
        const isValidSampleDesc = sampleDesc && sampleDesc !== '<br>';
        const isValidActivityDesc = activityDesc && activityDesc !== '<br>';

        // If both are empty or just "<br>", don't show the expand button
        if (!isValidSampleDesc && !isValidActivityDesc) {
          return null; // Don't render the panel
        }

        return (
          <Box
            sx={{
              bgcolor: '#f9f9f9',
              border: '1px solid #ccc',
              borderRadius: 2,
              boxShadow: 2,
              m: 2,
              p: 2,
            }}
          >
            {/* Only render Sample Description if it's non-empty */}
            {isValidSampleDesc ? (
              <>
                <Box sx={{ fontWeight: 'bold', fontSize: '1rem', mb: 1 }}>Sample Description:</Box>
                <Box
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.95rem',
                    mb: 2,
                    color: '#333',
                  }}
                  dangerouslySetInnerHTML={{ __html: sampleDesc }}
                />
              </>
            ) : null}

            {/* Only render Last Activity Description if it's non-empty */}
            {isValidActivityDesc ? (
              <>
                <Box sx={{ fontWeight: 'bold', fontSize: '1rem', mb: 1 }}>Last Activity Description:</Box>
                <Box sx={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>{activityDesc}</Box>
              </>
            ) : null}
          </Box>
        );
      }}
      getDetailPanelHeight={({ row }) => {
        // Get cleaned descriptions and check if they contain meaningful content
        const sampleDesc = removeLeadingTrailingBrTags(row.description || '');
        const activityDesc = row.lastActivityDesc?.trim();

        // Ignore descriptions with just empty tags like "<br>"
        const isValidSampleDesc = sampleDesc && sampleDesc !== '<br>';
        const isValidActivityDesc = activityDesc && activityDesc !== '<br>';

        // Adjust height dynamically based on how many descriptions are present
        if (isValidSampleDesc && isValidActivityDesc) {
          return 200; // Both descriptions present
        }
        if (isValidSampleDesc || isValidActivityDesc) {
          return 150; // One description present
        }
        return 0; // No descriptions, hide expand button
      }}
      sx={{
        ...dataGridStyle,
        '& .row-disabled': {
          opacity: 0.4,
          pointerEvents: 'none',
        },
      }}
    />
  );
};
